import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { useCallStore } from '@/stores/call-store';
import { useConversations, useUploadFile, useSendRichMessage } from '@/hooks/useMessenger';
import { getSocket } from '@/services/socket';

interface ComposerPlusPanelProps {
  threadId: string;
  open: boolean;
  onClose: () => void;
}

type ActionKey = 'voice' | 'video' | 'image' | 'camera' | 'file' | 'location';

interface PanelAction {
  key: ActionKey;
  icon: string;
  labelKey: string;
}

const ACTIONS: PanelAction[] = [
  { key: 'voice', icon: 'call', labelKey: 'composer_plus.voice' },
  { key: 'video', icon: 'videocam', labelKey: 'composer_plus.video' },
  { key: 'image', icon: 'image', labelKey: 'composer_plus.image' },
  { key: 'camera', icon: 'photo_camera', labelKey: 'composer_plus.camera' },
  { key: 'file', icon: 'attach_file', labelKey: 'composer_plus.file' },
  { key: 'location', icon: 'location_on', labelKey: 'composer_plus.location' },
];

export function ComposerPlusPanel({ threadId, open, onClose }: ComposerPlusPanelProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const startOutgoing = useCallStore((s) => s.startOutgoing);
  const { data: convs = [] } = useConversations();
  const conv = convs.find((c) => c.id === threadId);

  const upload = useUploadFile();
  const sendRich = useSendRichMessage();

  // Three separate inputs: "image" accepts image/*, "camera" forces the OS
  // camera via `capture`, "file" accepts anything. Native WebViews honor
  // these attrs and open the right picker / camera directly.
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function startCall(kind: 'audio' | 'video') {
    if (!user || !conv) {
      toast.error(t('composer_plus.call_no_target'));
      return;
    }
    try {
      const socket = await getSocket();
      await startOutgoing({
        socket,
        kind,
        remoteUserId: conv.id,
        remoteUserName: conv.name,
        localUserId: user.id,
        localUserName: user.displayName,
        conversationId: threadId,
      });
      onClose();
    } catch (e) {
      toast.error((e as Error).message || t('composer_plus.call_fail'));
    }
  }

  async function handleFile(file: File, kind: 'image' | 'file') {
    onClose();
    try {
      const meta = await upload.mutateAsync(file);
      const payload =
        kind === 'image'
          ? { kind: 'image' as const, ...meta }
          : { kind: 'file' as const, ...meta };
      await sendRich.mutateAsync([threadId, payload]);
    } catch (e) {
      toast.error((e as Error).message || t('composer_plus.upload_fail'));
    }
  }

  async function sendLocation() {
    if (!navigator.geolocation) {
      toast.error(t('composer_plus.location_unavailable'));
      return;
    }
    onClose();
    const loading = toast.loading(t('composer_plus.location_finding'));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        toast.dismiss(loading);
        try {
          await sendRich.mutateAsync([
            threadId,
            { kind: 'location', lat: pos.coords.latitude, lng: pos.coords.longitude },
          ]);
        } catch (e) {
          toast.error((e as Error).message || t('composer_plus.send_fail'));
        }
      },
      () => {
        toast.dismiss(loading);
        toast.error(t('composer_plus.location_denied'));
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30_000 },
    );
  }

  function onTap(action: PanelAction) {
    switch (action.key) {
      case 'voice':
        void startCall('audio');
        break;
      case 'video':
        void startCall('video');
        break;
      case 'image':
        imageInputRef.current?.click();
        break;
      case 'camera':
        cameraInputRef.current?.click();
        break;
      case 'file':
        fileInputRef.current?.click();
        break;
      case 'location':
        void sendLocation();
        break;
    }
  }

  if (!open) return null;

  return (
    <>
      <div className="composer-plus-overlay" onClick={onClose} role="presentation" />
      <div className="composer-plus-panel" role="menu">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            type="button"
            className="composer-plus-tile"
            onClick={() => onTap(a)}
            role="menuitem"
          >
            <span className="composer-plus-icon">
              <span className="material-symbols-outlined">{a.icon}</span>
            </span>
            <span className="composer-plus-label">{t(a.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Hidden file pickers — triggered by tile handlers */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f, 'image');
          e.target.value = '';
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f, 'image');
          e.target.value = '';
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f, 'file');
          e.target.value = '';
        }}
      />
    </>
  );
}
